/**
 * Step 2 - Escolha do Plano.
 *
 * Réplica do frame `web-step3-plano` (o nome do frame guarda a numeração antiga;
 * a v6 §10 traz o plano para antes dos dependentes): os planos ficam lado a lado
 * em cards, cada um com o total aproximado em destaque. O card selecionado ganha
 * borda da marca, fundo tingido e o check no canto; o detalhe por pessoa (nome,
 * idade, faixa, valor) aparece uma vez, abaixo dos cards, para o plano
 * selecionado.
 *
 * O total é somado no browser sobre os dependentes mantidos. Pessoa sem preço
 * entra como "a confirmar" e o plano continua selecionável (spec 5.2).
 */

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Alert } from "@mui/material";
import styled from "styled-components";

import { formatTotalText, PriceBreakdown } from "../components/PriceBreakdown";
import { StatusChip } from "../components/StatusChip";
import { StepActions } from "../components/StepActions";
import { useExtensionForm } from "../context";
import {
  Caption,
  Divider,
  Header,
  Paragraph,
  Row,
  SectionLabel,
  Stack,
} from "../styled";
import { COLORS, MOBILE_MEDIA_QUERY } from "../tokens";

/** Os três cards ficam lado a lado no web e empilham no mobile. */
const PlanGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  ${MOBILE_MEDIA_QUERY} {
    grid-template-columns: 1fr;
  }
`;

const PlanCard = styled.button<{ $selected: boolean }>`
  position: relative;
  width: 100%;
  text-align: left;
  cursor: pointer;
  font: inherit;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  padding: 16px;
  border-radius: 16px;
  border: solid ${({ $selected }) => ($selected ? "2px" : "1px")}
    ${({ $selected }) => ($selected ? COLORS.brand : COLORS.border)};
  background-color: ${({ $selected }) =>
    $selected ? COLORS.brandTint : COLORS.surface};
  transition: border-color 0.2s, background-color 0.2s;
`;

const SelectedCheck = styled(CheckCircleIcon)`
  position: absolute;
  top: 12px;
  right: 12px;
  color: ${COLORS.brand};
`;

const PlanPrice = styled.strong`
  color: ${COLORS.ink};
  font-size: 18px;
  font-weight: 700;
  line-height: 130%;
`;

export function Step2Plan() {
  const { data, state, updateState, goNext, goBack } = useExtensionForm();
  const { eligiblePlans } = data;

  const retained = state.retainedDependentTaxPayerIds;

  const selectedPlan = eligiblePlans.find(
    (plan) => plan.id === state.chosenPlanId
  );

  return (
    <Stack $gap="24px">
      <Stack $gap="8px">
        <Row $gap="12px">
          <Header>Escolha do Plano</Header>
          {selectedPlan?.isCurrentPlan && (
            <StatusChip tone="infoSolid">Seu plano atual</StatusChip>
          )}
        </Row>
        <Paragraph>
          Seu plano atual está pré-selecionado. Você pode mantê-lo ou escolher
          outra opção disponível com base na rede de atendimento desejada.
        </Paragraph>
      </Stack>

      <PlanGrid>
        {eligiblePlans.map((plan) => {
          const isSelected = plan.id === state.chosenPlanId;

          return (
            <PlanCard
              key={plan.id}
              type="button"
              $selected={isSelected}
              aria-pressed={isSelected}
              onClick={() => updateState({ chosenPlanId: plan.id })}
            >
              {isSelected && <SelectedCheck />}

              <StatusChip tone="infoSolid">
                {plan.isCurrentPlan ? "Seu plano atual" : "Disponível"}
              </StatusChip>

              <Stack $gap="2px">
                <SectionLabel>{plan.name}</SectionLabel>
                <Caption>{plan.code}</Caption>
              </Stack>

              <PlanPrice>{formatTotalText(plan, retained)}</PlanPrice>
            </PlanCard>
          );
        })}
      </PlanGrid>

      {eligiblePlans.length === 0 && (
        <Alert severity="warning">
          Nenhum plano elegível foi encontrado para esta oferta. Entre em contato
          com o RH da {data.offer.companyName}.
        </Alert>
      )}

      {!!selectedPlan && (
        <>
          <Divider />

          <PriceBreakdown
            plan={selectedPlan}
            holder={data.holder}
            dependents={data.dependents}
            retainedDependentTaxPayerIds={retained}
          />
        </>
      )}

      <Alert severity="info">
        Valor aproximado, pela idade de cada pessoa hoje e pela tabela de
        inativos do contrato. O valor final é definido pela operadora.
      </Alert>

      <Alert severity="info">
        A cobrança é feita pela operadora diretamente a você.
      </Alert>

      <Alert severity="info">
        Lembre-se: os valores apresentados consideram os dependentes que serão
        mantidos. Você define essa lista no próximo passo, e o total é
        recalculado automaticamente.
      </Alert>

      <StepActions
        nextLabel="Próximo: Dependentes →"
        onNext={goNext}
        onBack={goBack}
        isNextDisabled={!selectedPlan}
      />
    </Stack>
  );
}
